/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {Box, Link, Text, useSlotRecipe} from '@chakra-ui/react3'
import {Global} from '@emotion/react'
import {Link as RouterLink} from 'react-router-dom'

import {BrandLogoBlack} from '../../../../components/icons'
import {useCurrentCustomer} from '../../../../hooks/use-current-customer'
import useMultiSite from '../../../../hooks/use-multi-site'
import {ACCOUNT_HREF, HOME_HREF} from '../../../../constants'
import {checkoutHeaderRecipe} from './index.recipe'
import {HeaderVariant} from '../header/header-variant'
import {SquareLockIcon} from '../../icons'

/**
 * The checkout page background, published as the page canvas so the header's
 * card gutter shows it through rather than the header painting a band of its
 * own inside its sticky root (STOK-17630).
 */
export const CHECKOUT_PAGE_BG = 'background.checkout'

const CheckoutHeader = React.forwardRef(({isCheckoutConfirmation = false}, ref) => {
    const {formatMessage} = useIntl()
    const {buildUrl} = useMultiSite()
    const {data: customer} = useCurrentCustomer()

    const recipe = useSlotRecipe({recipe: checkoutHeaderRecipe})
    const styles = useMemo(() => recipe(), [recipe])

    const homeUrl = useMemo(() => buildUrl(HOME_HREF), [buildUrl])
    const accountUrl = useMemo(() => buildUrl(ACCOUNT_HREF), [buildUrl])

    const showAccountLink = Boolean(customer?.isRegistered && isCheckoutConfirmation)

    return (
        <>
            {/* Publish this header's own footprint into the height chain
                (app/leap/theme/global-css.js). */}
            <Global
                styles={{
                    ':root': {'--header-bar-block-size': 'var(--checkout-header-bar-block-size)'}
                }}
            />

            <HeaderVariant bg={CHECKOUT_PAGE_BG} />

            <Box as="header" ref={ref} css={styles.root}>
                <Box css={styles.cardContainer}>
                    <Box css={styles.card}>
                        <Box css={styles.bar}>
                            <Box css={styles.barInner} layerStyle="container.page">
                                <Link
                                    as={RouterLink}
                                    to={homeUrl}
                                    aria-label={formatMessage({
                                        id: 'header.button.assistive_msg.logo',
                                        defaultMessage: 'Stokke homepage'
                                    })}
                                    css={styles.logoLink}
                                >
                                    <BrandLogoBlack
                                        css={styles.logoIcon}
                                        aria-hidden="true"
                                        focusable="false"
                                    />
                                </Link>

                                <Box css={styles.rightSection}>
                                    {showAccountLink ? (
                                        <Link
                                            as={RouterLink}
                                            to={accountUrl}
                                            variant="secondary"
                                            size="xsmall"
                                            css={styles.accountLink}
                                        >
                                            <FormattedMessage
                                                id="global.my_account"
                                                defaultMessage="My Stokke"
                                            />
                                        </Link>
                                    ) : (
                                        <>
                                            <Text css={styles.secureLabel}>
                                                <FormattedMessage
                                                    id="global.secure_checkout"
                                                    defaultMessage="Secure Checkout"
                                                />
                                            </Text>

                                            <SquareLockIcon
                                                css={styles.lockIcon}
                                                aria-hidden="true"
                                                focusable="false"
                                                data-testid="lock-logo"
                                            />
                                        </>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </>
    )
})

if (process.env.NODE_ENV !== 'production') {
    CheckoutHeader.displayName = 'CheckoutHeader'
}

CheckoutHeader.propTypes = {
    isCheckoutConfirmation: PropTypes.bool
}

export default CheckoutHeader
